import React, { useState } from 'react';

// ─── Constants ──────────────────────────────────────────────────────────────

const POSITION_LABELS = {
  QB:  'QB',
  RB:  'RB',
  WR:  'WR',
  TE:  'TE',
  FLEX: 'FLEX (W/R/T)',
  K:   'K',
  DEF: 'DEF / ST',
  BN:  'Bench',
};

const CANONICAL_KEYS = ['QB', 'RB', 'WR', 'TE', 'FLEX', 'K', 'DEF', 'BN'];

// ─── Main Component ───────────────────────────────────────────────────────────

/**
 * Displays and optionally edits a league's starting-lineup slot requirements
 * (rosterConfig) -- e.g. how many QBs/RBs/etc. a starting lineup requires.
 * This is the same signal AI features (Start/Sit, Trade Analysis) use to reason
 * about positional scarcity, so an auto-detected value that came back wrong or
 * empty (e.g. an import whose live payload didn't match the parser's assumed
 * shape) can be corrected here directly.
 *
 * @param {Object}   rosterConfig - Map of position key -> starting-slot count
 * @param {Function} onChange     - Called with the updated map on any change
 * @param {boolean}  readOnly     - When true, renders plain text values instead of inputs
 */
export default function RosterConfigEditor({ rosterConfig = {}, onChange, readOnly = false }) {
  const [newPositionKey, setNewPositionKey] = useState('');

  const extraKeys = Object.keys(rosterConfig)
    .filter(k => !CANONICAL_KEYS.includes(k))
    .sort();
  const isEmpty = Object.keys(rosterConfig).length === 0;

  function handleCountChange(key, value) {
    const count = parseInt(value, 10);
    onChange({ ...rosterConfig, [key]: Number.isNaN(count) ? 0 : count });
  }

  function handleAddPosition() {
    const key = newPositionKey.trim().toUpperCase();
    if (!key || key in rosterConfig) return;
    onChange({ ...rosterConfig, [key]: 1 });
    setNewPositionKey('');
  }

  function handleRemovePosition(key) {
    const next = { ...rosterConfig };
    delete next[key];
    onChange(next);
  }

  function renderRow(key, removable) {
    return (
      <div key={key} className='scoring-row'>
        <span className='scoring-label'>{POSITION_LABELS[key] ?? key}</span>
        {readOnly ? (
          <span className='scoring-value'>{rosterConfig[key] ?? 0}</span>
        ) : (
          <>
            <input
              type='number'
              min='0'
              className='scoring-input'
              value={rosterConfig[key] ?? 0}
              onChange={e => handleCountChange(key, e.target.value)}
            />
            {removable && (
              <button
                type='button'
                className='btn btn-secondary'
                style={{ marginLeft: 8, padding: '2px 8px' }}
                onClick={() => handleRemovePosition(key)}
                aria-label={`Remove ${key}`}
              >
                ✕
              </button>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <div className='scoring-rules-editor'>
      {readOnly && isEmpty && (
        <p style={{ color: 'var(--text-muted)' }}>
          Not set — this league's starting-lineup shape hasn't been recorded yet.
        </p>
      )}

      {(!isEmpty || !readOnly) && (
        <div className='scoring-section'>
          {CANONICAL_KEYS.filter(k => k in rosterConfig || !readOnly).map(k => renderRow(k, false))}
          {extraKeys.map(k => renderRow(k, !readOnly))}
        </div>
      )}

      {!readOnly && (
        <div className='scoring-row' style={{ marginTop: 8 }}>
          <input
            type='text'
            className='search-input'
            placeholder='Add position (e.g. DL)'
            value={newPositionKey}
            onChange={e => setNewPositionKey(e.target.value)}
            style={{ maxWidth: 220 }}
          />
          <button type='button' className='btn btn-secondary' onClick={handleAddPosition}>
            Add
          </button>
        </div>
      )}
    </div>
  );
}
